import React, { memo, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Stack,
  Typography,
  IconButton,
  Button,
  Icon,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Card
} from 'investira.react.components';
import { BaseTemplate } from '../../../components/templates';
import withMessage from '../../../hoc/withMessage';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import { AVALIACAO_RESPOSTA_TIPO } from '../../../enums';
import services from '../../../services';
import Range from '../_components/Range';
import QuestaoRating from '../_components/QuestaoRating';
import {
  createOpcoesDicotomicas,
  mapRespostaDominioToOpcoesDicotomicas,
  mapOpcoesDicotomicasToRespostaDominio
} from '../_shared/dicotomicaOptions';
import getQuestaoInternaFromApi from '../_shared/getQuestaoInternaFromApi';

// Limite visual/funcional de opções e faixas suportadas na tela.
const MAX_DISCRIMINACOES = 13;

// Gera o rótulo ordinal exibido na UI (1ª, 2ª, 3ª...).
const getOpcaoPorIndice = pIndice => `${pIndice}ª` || '';

// Lista fixa usada apenas para exibição dos títulos das opções.
const OPCOES_DISCRIMINACAO = Array(MAX_DISCRIMINACOES)
  .fill()
  .map((_, xIndex) => getOpcaoPorIndice(xIndex + 1));

// Cria uma opção padrão de múltipla escolha para inicialização e inclusão dinâmica.
const createMultiplaEscolhaItem = pIndex => ({
  id: Date.now() + pIndex,
  opcao: getOpcaoPorIndice(pIndex + 1),
  pergunta: '',
  discriminacao: '',
  points: -3,
  rating: 1
});

// Cria uma faixa numérica padrão para inicialização e inclusão dinâmica.
const createFaixaNumerica = pIndex => ({
  id: Date.now() + pIndex,
  faixa: `${pIndex + 1}ª`,
  min: '',
  max: '',
  discriminacao: '',
  points: -3,
  rating: 1
});

// Converte a nota da API/UI para número; fallback padrão mantém comportamento atual do Range.
const parseRespostaNota = pValor => {
  const xNumero = Number(pValor);
  return Number.isFinite(xNumero) ? xNumero : -3;
};

// Converte limites numéricos mantendo string vazia quando não houver valor.
const parseRespostaLimite = pValor => {
  if (pValor === '' || pValor === null || pValor === undefined) {
    return '';
  }

  const xNumero = Number(pValor);
  return Number.isFinite(xNumero) ? xNumero : '';
};

// Normaliza o retorno do list para um único item, aceitando array, objeto único ou coleção indexada.
const normalizeQuestaoData = pData => {
  if (Array.isArray(pData)) {
    return pData[0] || null;
  }

  if (pData && typeof pData === 'object') {
    if (pData.avaliacao_questao_id || pData.id) {
      return pData;
    }

    const xFirstItem = Object.values(pData)[0];
    return xFirstItem || null;
  }

  return null;
};

/**
 * Tela de edição de Questões das Avaliações.
 *
 * Responsabilidades:
 * - carregar seções e dados da questão por id;
 * - hidratar o formulário no formato da UI;
 * - validar regras locais por tipo de resposta;
 * - persistir edição no serviço `questoes.modify`.
 */
const AvaliacoesSettingsQuestoesEdit = memo(props => {
  const navigate = useNavigate();
  const params = useParams();
  const xQuestaoId = Number(params.id);

  const [sessoesQuestoes, setSessoesQuestoes] = useState([]);
  const [tipoRespostaHidratado, setTipoRespostaHidratado] = useState(null);

  const { actions } = useAvaliacoesContext();

  const formik = useFormik({
    initialValues: {
      questao: '',
      tooltip: '',
      sessaoId: '',
      tipoResposta: AVALIACAO_RESPOSTA_TIPO.MUL.id,
      interna: true,
      opcoesMultiplaEscolha: [createMultiplaEscolhaItem(0)],
      opcoesDicotomicas: createOpcoesDicotomicas(),
      faixasNumericas: [createFaixaNumerica(0)]
    },
    validateOnMount: false,
    // Submit centralizado: valida o formulário, traduz para payload de API e persiste edição.
    onSubmit: pValues => {
      const isQuestaoInterna = pValues.interna;

      // Crítica obrigatória: título da questão é sempre requerido.
      if (!pValues.questao.trim()) {
        props.onMessageError?.('Informe o título da questão.');
        return;
      }

      // Crítica obrigatória: seção da questão deve estar selecionada.
      if (!pValues.sessaoId) {
        props.onMessageError?.('Selecione a sessão da questão.');
        return;
      }

      // Em múltipla escolha exibida ao gestor, toda opção precisa de texto de pergunta.
      if (
        !isQuestaoInterna &&
        pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id &&
        pValues.opcoesMultiplaEscolha.some(xOpcao => !xOpcao.pergunta?.trim())
      ) {
        props.onMessageError?.('Preencha a pergunta de todas as opções de múltipla escolha.');
        return;
      }

      // Múltipla escolha requer ao menos uma opção.
      if (
        pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id &&
        pValues.opcoesMultiplaEscolha.length === 0
      ) {
        props.onMessageError?.('Adicione ao menos uma opção de múltipla escolha.');
        return;
      }

      // Toda opção precisa de pontuação definida.
      if (pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id) {
        const xOpcaoSemPontos = pValues.opcoesMultiplaEscolha.find(
          xOpcao => xOpcao.points === null
        );
        if (xOpcaoSemPontos) {
          props.onMessageError?.('Selecione a pontuação de todas as opções.');
          return;
        }
      }

      // Dicotômica requer nota para Sim e Não.
      if (!isQuestaoInterna && pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id) {
        const xOpcaoSemPontos = pValues.opcoesDicotomicas.find(xOpcao => xOpcao.points === null);
        if (xOpcaoSemPontos) {
          props.onMessageError?.('Selecione a pontuação das duas opções dicotômicas.');
          return;
        }
      }

      // Numérica requer ao menos uma faixa.
      if (
        !isQuestaoInterna &&
        pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id &&
        pValues.faixasNumericas.length === 0
      ) {
        props.onMessageError?.('Adicione ao menos uma faixa numérica.');
        return;
      }

      // Regras de consistência para faixas numéricas (vazios permitidos só nas extremidades).
      if (!isQuestaoInterna && pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id) {
        const xFaixas = pValues.faixasNumericas;
        const xLastIndex = xFaixas.length - 1;

        const xFaixaSemPontos = xFaixas.find(xFaixa => xFaixa.points === null);
        if (xFaixaSemPontos) {
          props.onMessageError?.('Selecione a pontuação de todas as faixas.');
          return;
        }

        for (let xIndex = 0; xIndex < xFaixas.length; xIndex += 1) {
          const xFaixa = xFaixas[xIndex];
          const xMinPodeSerNulo = xIndex === 0;
          const xMaxPodeSerNulo = xIndex === xLastIndex;
          const xMinVazio = xFaixa.min === '' || xFaixa.min === null || xFaixa.min === undefined;
          const xMaxVazio = xFaixa.max === '' || xFaixa.max === null || xFaixa.max === undefined;

          if (!xMinPodeSerNulo && xMinVazio) {
            props.onMessageError?.(`Informe o mínimo da faixa ${xIndex + 1}.`);
            return;
          }

          if (!xMaxPodeSerNulo && xMaxVazio) {
            props.onMessageError?.(`Informe o máximo da faixa ${xIndex + 1}.`);
            return;
          }

          if (!xMinVazio) {
            const xMin = Number(xFaixa.min);
            if (!Number.isFinite(xMin) || xMin < 0) {
              props.onMessageError?.(
                `O mínimo da faixa ${xIndex + 1} deve ser numérico e não-negativo.`
              );
              return;
            }
          }

          if (!xMaxVazio) {
            const xMax = Number(xFaixa.max);
            if (!Number.isFinite(xMax) || xMax < 0) {
              props.onMessageError?.(
                `O máximo da faixa ${xIndex + 1} deve ser numérico e não-negativo.`
              );
              return;
            }
          }

          if (!xMinVazio && !xMaxVazio && Number(xFaixa.min) > Number(xFaixa.max)) {
            props.onMessageError?.(`A faixa ${xIndex + 1} está com mínimo maior que máximo.`);
            return;
          }

          if (xIndex > 0 && !xMinVazio) {
            const xFaixaAnterior = xFaixas[xIndex - 1];
            const xMaxAnteriorVazio =
              xFaixaAnterior.max === '' ||
              xFaixaAnterior.max === null ||
              xFaixaAnterior.max === undefined;

            if (!xMaxAnteriorVazio && Number(xFaixa.min) < Number(xFaixaAnterior.max)) {
              props.onMessageError?.(
                'O mínimo de um item não pode ser menor que o máximo do item anterior.'
              );
              return;
            }
          }
        }
      }

      // Traduz o estado do formulário para o contrato `resposta_dominio` da API.
      const xRespostaDominio =
        isQuestaoInterna || pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
          ? pValues.opcoesMultiplaEscolha.map((xOpcao, xIndex) => ({
              id: xIndex + 1,
              label: isQuestaoInterna ? `Opção ${xIndex + 1}` : (xOpcao.pergunta || '').trim(),
              tooltip: '',
              objetivo: (xOpcao.discriminacao || '').trim(),
              nota: Number(xOpcao.points)
            }))
          : pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id
          ? mapOpcoesDicotomicasToRespostaDominio(pValues.opcoesDicotomicas)
          : pValues.faixasNumericas.map((xFaixa, xIndex) => {
              const xMinVazio =
                xFaixa.min === '' || xFaixa.min === null || xFaixa.min === undefined;
              const xMaxVazio =
                xFaixa.max === '' || xFaixa.max === null || xFaixa.max === undefined;

              return {
                id: xIndex + 1,
                label: `${xFaixa.faixa} Opção`,
                tooltip: '',
                objetivo: (xFaixa.discriminacao || '').trim(),
                minimo: xMinVazio ? null : Number(xFaixa.min),
                maximo: xMaxVazio ? null : Number(xFaixa.max),
                valor: xIndex,
                nota: Number(xFaixa.points)
              };
            });

      // Payload final de edição com identificador explícito da questão.
      const xPayload = {
        id: xQuestaoId,
        avaliacao_questao_id: xQuestaoId,
        avaliacao_questao: pValues.questao.trim(),
        avaliacao_questao_tooltip: (pValues.tooltip || '').trim(),
        avaliacao_secao_id: Number(pValues.sessaoId),
        resposta_tipo: isQuestaoInterna ? null : pValues.tipoResposta,
        resposta_dominio: xRespostaDominio
      };

      services.questoes.modify(
        xPayload,
        () => {
          props.onMessageSuccess?.('Questão atualizada com sucesso.');
          actions.handleChangeActiveSessaoId(pValues.sessaoId);
          navigate('/avaliacoes/configuracoes/questoes');
        },
        rErr => {
          props.onMessageError?.(rErr?.message || 'Não foi possível atualizar a questão.');
        }
      );
    }
  });

  const { values } = formik;
  const isQuestaoInterna = values.interna;
  const xSessaoSelecionadaValida = sessoesQuestoes.some(
    xSessao => xSessao.sessao_id === Number(values.sessaoId)
  );

  // Carrega seções disponíveis para alimentar o Select lateral.
  useEffect(() => {
    services.secoes.list(
      {
        page: 1,
        size: 100,
        sort: 'avaliacao_secao_id asc'
      },
      rRes => {
        const xSessoesFromService = Object.values(rRes?.data || {})
          .map(xSessao => ({
            sessao_id: Number(xSessao?.sessao_id ?? xSessao?.avaliacao_secao_id),
            sessao: xSessao?.sessao ?? xSessao?.avaliacao_secao
          }))
          .filter(xSessao => Number.isFinite(xSessao.sessao_id) && xSessao.sessao);

        if (xSessoesFromService.length) {
          setSessoesQuestoes(xSessoesFromService);
        }
      }
    );
  }, []);

  // Carrega dados da questão e hidrata o formulário com API como fonte de verdade.
  useEffect(() => {
    // Proteção de rota: id inválido não deve permanecer na tela de edição.
    if (!Number.isFinite(xQuestaoId) || xQuestaoId <= 0) {
      props.onMessageError?.('ID da questão inválido para edição.');
      navigate('/avaliacoes/configuracoes/questoes');
      return;
    }

    services.questoes.list(
      { avaliacao_questao_id: xQuestaoId },
      rRes => {
        const xQuestao = normalizeQuestaoData(rRes?.data);

        // Se o backend não encontrar o registro, retorna para a listagem com crítica.
        if (!xQuestao) {
          props.onMessageError?.('Questão não encontrada para edição.');
          navigate('/avaliacoes/configuracoes/questoes');
          return;
        }

        const xQuestaoInterna = getQuestaoInternaFromApi(xQuestao);
        const xTipoResposta =
          xQuestao?.resposta_tipo ??
          (xQuestaoInterna ? null : AVALIACAO_RESPOSTA_TIPO.MUL.id);
        const xRespostaDominio = Array.isArray(xQuestao?.resposta_dominio)
          ? xQuestao.resposta_dominio
          : Object.values(xQuestao?.resposta_dominio || {});

        // Hidratação de múltipla escolha.
        const xMultiplaEscolha = xRespostaDominio.length
          ? xRespostaDominio.map((xItem, xIndex) => ({
              id: xItem?.id || `mul-${xIndex + 1}`,
              opcao: getOpcaoPorIndice(xIndex + 1),
              pergunta: (xItem?.label || '').trim(),
              discriminacao: (xItem?.objetivo || '').trim(),
              points: parseRespostaNota(xItem?.nota),
              rating: 1
            }))
          : [createMultiplaEscolhaItem(0)];

        const xDicotomicas = mapRespostaDominioToOpcoesDicotomicas(xRespostaDominio);

        // Hidratação de faixas numéricas.
        const xFaixasNumericas = xRespostaDominio.length
          ? xRespostaDominio.map((xItem, xIndex) => ({
              id: xItem?.id || `num-${xIndex + 1}`,
              faixa: `${xIndex + 1}ª`,
              min: parseRespostaLimite(xItem?.minimo),
              max: parseRespostaLimite(xItem?.maximo),
              discriminacao: (xItem?.objetivo || '').trim(),
              points: parseRespostaNota(xItem?.nota),
              rating: 1
            }))
          : [createFaixaNumerica(0)];

        // Reescreve integralmente o formulário para refletir exatamente o estado do backend.
        formik.setValues({
          questao: xQuestao?.avaliacao_questao || '',
          tooltip: xQuestao?.avaliacao_questao_tooltip || '',
          sessaoId: Number(xQuestao?.avaliacao_secao_id) || formik.values.sessaoId,
          tipoResposta: xTipoResposta,
          interna: xQuestaoInterna,
          opcoesMultiplaEscolha: xMultiplaEscolha,
          opcoesDicotomicas: xDicotomicas,
          faixasNumericas: xFaixasNumericas
        });
        setTipoRespostaHidratado(xTipoResposta);
      },
      rErr => {
        props.onMessageError?.(rErr?.message || 'Não foi possível carregar a questão para edição.');
        navigate('/avaliacoes/configuracoes/questoes');
      }
    );
  }, [xQuestaoId]);

  // Atualiza um campo pontual de opção de múltipla escolha.
  const handleUpdateMultiplaEscolha = (id, field, value) => {
    formik.setFieldValue(
      'opcoesMultiplaEscolha',
      values.opcoesMultiplaEscolha.map(xOpcao => {
        if (xOpcao.id !== id) {
          return xOpcao;
        }
        return { ...xOpcao, [field]: value };
      })
    );
  };

  // Inclui nova opção de múltipla escolha respeitando o limite máximo.
  const handleAddMultiplaEscolha = () => {
    if (values.opcoesMultiplaEscolha.length >= MAX_DISCRIMINACOES) {
      return;
    }

    formik.setFieldValue('opcoesMultiplaEscolha', [
      ...values.opcoesMultiplaEscolha,
      createMultiplaEscolhaItem(values.opcoesMultiplaEscolha.length)
    ]);
  };

  // Remove opção e reindexa os rótulos ordinais remanescentes.
  const handleRemoveMultiplaEscolha = id => {
    if (values.opcoesMultiplaEscolha.length <= 1) {
      return;
    }

    formik.setFieldValue(
      'opcoesMultiplaEscolha',
      values.opcoesMultiplaEscolha
        .filter(xOpcao => xOpcao.id !== id)
        .map((xOpcao, xIndex) => ({
          ...xOpcao,
          opcao: getOpcaoPorIndice(xIndex + 1)
        }))
    );
  };

  // Atualiza um campo pontual de faixa numérica.
  const handleUpdateFaixaNumerica = (id, field, value) => {
    formik.setFieldValue(
      'faixasNumericas',
      values.faixasNumericas.map(xFaixa => {
        if (xFaixa.id !== id) {
          return xFaixa;
        }
        return { ...xFaixa, [field]: value };
      })
    );
  };

  // Inclui nova faixa respeitando o limite máximo.
  const handleAddFaixaNumerica = () => {
    if (values.faixasNumericas.length >= MAX_DISCRIMINACOES) {
      return;
    }

    formik.setFieldValue('faixasNumericas', [
      ...values.faixasNumericas,
      createFaixaNumerica(values.faixasNumericas.length)
    ]);
  };

  // Remove faixa e reindexa os rótulos ordinais remanescentes.
  const handleRemoveFaixaNumerica = id => {
    if (values.faixasNumericas.length <= 1) {
      return;
    }

    formik.setFieldValue(
      'faixasNumericas',
      values.faixasNumericas
        .filter(xFaixa => xFaixa.id !== id)
        .map((xFaixa, xIndex) => ({
          ...xFaixa,
          faixa: `${xIndex + 1}ª`
        }))
    );
  };

  // Atualiza um campo pontual da opção dicotômica.
  const handleUpdateDicotomica = (id, field, value) => {
    formik.setFieldValue(
      'opcoesDicotomicas',
      values.opcoesDicotomicas.map(xOpcao =>
        xOpcao.id === id ? { ...xOpcao, [field]: value } : xOpcao
      )
    );
  };

  const handleChangeInterna = pChecked => {
    formik.setFieldValue('interna', pChecked);
    formik.setFieldValue(
      'tipoResposta',
      pChecked
        ? null
        : values.tipoResposta ||
            tipoRespostaHidratado ||
            AVALIACAO_RESPOSTA_TIPO.MUL.id
    );
  };

  // Aciona o submit do Formik a partir dos botões de ação da tela.
  const handleSave = () => formik.submitForm();

  return (
    <BaseTemplate
      title={'Editar Questão'}
      subtitle={'Avaliações'}
      description="Defina o peso de cada questão conforme sua relevância para a avaliação. Os pesos auxiliam na priorização dos critérios e permitem personalizar o impacto das respostas no resultado final."
      maxWidth={'xl'}
      headerRenderActions={
        <Stack direction="row" spacing={2}>
          <Button
            disabled={props.disabledButtons}
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="upload" size={16} />}
            size="small"
            sx={{ px: 2 }}
            onClick={handleSave}>
            Salvar
          </Button>
        </Stack>
      }>
      <Divider />
      {/* Layout principal: conteúdo editável à esquerda e metadados/ações à direita. */}
      <Stack direction="row" gap={4} py={3}>
        {/* Coluna principal: título, tipo de resposta e itens de avaliação. */}
        <Stack component="main" flexGrow={1} pt={3} gap={2}>
          <Stack gap={2}>
            <TextField
              label="Título"
              placeholder="Ex.: Classificação de qualidade de gestão [agências de rating]"
              value={values.questao}
              onChange={formik.handleChange('questao')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              multiline
              rows={4}
            />
            <TextField
              label="Tooltip"
              placeholder="Informações complementares da questão"
              value={values.tooltip}
              onChange={formik.handleChange('tooltip')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              multiline
              rows={2}
            />
          </Stack>

          <Divider />

          <Stack gap={2}>
            <Stack gap={2} direction="row">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.interna}
                      onChange={e => handleChangeInterna(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography color="textPrimary" variant="body2">
                      Questão interna
                    </Typography>
                  }
                />
              </FormGroup>
            </Stack>

            <Divider />
            {!isQuestaoInterna && (
              <>
                <Stack gap={1}>
                  <Typography
                    color="textPrimary"
                    variant="caption"
                    sx={{ textTransform: 'uppercase' }}>
                    TIPO DE RESPOSTA
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <Button
                      size="small"
                      disabled={
                        isQuestaoInterna ||
                        (tipoRespostaHidratado &&
                          tipoRespostaHidratado !== AVALIACAO_RESPOSTA_TIPO.MUL.id)
                      }
                      variant={
                        values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() =>
                        formik.setFieldValue('tipoResposta', AVALIACAO_RESPOSTA_TIPO.MUL.id)
                      }>
                      Múltipla escolha
                    </Button>
                    <Button
                      size="small"
                      disabled={
                        isQuestaoInterna ||
                        (tipoRespostaHidratado &&
                          tipoRespostaHidratado !== AVALIACAO_RESPOSTA_TIPO.NUM.id)
                      }
                      variant={
                        values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() =>
                        formik.setFieldValue('tipoResposta', AVALIACAO_RESPOSTA_TIPO.NUM.id)
                      }>
                      Numérica
                    </Button>
                    <Button
                      size="small"
                      disabled={
                        isQuestaoInterna ||
                        (tipoRespostaHidratado &&
                          tipoRespostaHidratado !== AVALIACAO_RESPOSTA_TIPO.DIC.id)
                      }
                      variant={
                        values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() =>
                        formik.setFieldValue('tipoResposta', AVALIACAO_RESPOSTA_TIPO.DIC.id)
                      }>
                      Dicotômica
                    </Button>
                  </Stack>
                </Stack>

                <Divider />
              </>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography color="textPrimary" variant="h6">
                {values.interna && values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id
                  ? 'Faixas numéricas'
                  : values.interna && values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id
                  ? 'Opções dicotômicas'
                  : isQuestaoInterna
                  ? 'Avaliação interna'
                  : 'Avaliação'}
              </Typography>
            </Stack>

            {/* Bloco dinâmico de tipo: múltipla escolha, dicotômica ou numérica. */}
            {(isQuestaoInterna || values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id) &&
              values.opcoesMultiplaEscolha.map((xOpcao, xIndex) => (
                <Stack
                  key={xOpcao.id}
                  gap={1.5}
                  sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      <b>{OPCOES_DISCRIMINACAO[xIndex]} Opção</b>
                    </Typography>

                    <IconButton
                      color="error"
                      size="small"
                      disabled={values.opcoesMultiplaEscolha.length === 1}
                      onClick={() => handleRemoveMultiplaEscolha(xOpcao.id)}>
                      <Icon iconName="delete" color="inherit" size={21} />
                    </IconButton>
                  </Stack>
                  <Divider />
                  <Stack gap={3}>
                    <TextField
                      label={isQuestaoInterna ? 'Critério interno' : 'Discriminação'}
                      placeholder="Critério interno"
                      value={xOpcao.discriminacao}
                      onChange={pEvent =>
                        handleUpdateMultiplaEscolha(xOpcao.id, 'discriminacao', pEvent.target.value)
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                    {!values.interna && (
                      <TextField
                        label="Resposta"
                        placeholder="Texto exibido para resposta"
                        value={xOpcao.pergunta}
                        onChange={pEvent =>
                          handleUpdateMultiplaEscolha(xOpcao.id, 'pergunta', pEvent.target.value)
                        }
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Stack>
                    <Range
                      value={xOpcao.points}
                      onChange={pValue => handleUpdateMultiplaEscolha(xOpcao.id, 'points', pValue)}
                    />
                    <QuestaoRating
                      readOnly
                      rating={xIndex}
                      max={xIndex === 0 ? 1 : values.opcoesMultiplaEscolha.length - 1}
                    />
                  </Stack>
                </Stack>
              ))}

            {!values.interna &&
              values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id &&
              values.opcoesDicotomicas.map((xOpcao, xIndex) => (
                <Stack
                  key={xOpcao.id}
                  gap={1.5}
                  sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ minHeight: '31px' }}>
                    <Typography variant="body2" color="textSecondary">
                      <b>Opção {xOpcao.opcao}</b>
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack gap={3}>
                    <TextField
                      label="Discriminação"
                      placeholder="Critério interno"
                      value={xOpcao.discriminacao}
                      onChange={pEvent =>
                        handleUpdateDicotomica(xOpcao.id, 'discriminacao', pEvent.target.value)
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Stack>
                  <Stack>
                    <Range
                      value={xOpcao.points}
                      onChange={pValue => handleUpdateDicotomica(xOpcao.id, 'points', pValue)}
                    />
                    <QuestaoRating
                      readOnly
                      rating={xIndex}
                      max={xIndex === 0 ? 1 : values.opcoesDicotomicas.length - 1}
                    />
                  </Stack>
                </Stack>
              ))}

            {!values.interna &&
              values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id &&
              values.faixasNumericas.map((xFaixa, xIndex) => (
                <Stack
                  key={xFaixa.id}
                  gap={1.5}
                  sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      <b>{xFaixa.faixa} Opção</b>
                    </Typography>

                    <IconButton
                      color="error"
                      size="small"
                      disabled={values.faixasNumericas.length === 1}
                      onClick={() => handleRemoveFaixaNumerica(xFaixa.id)}>
                      <Icon iconName="delete" color="inherit" size={21} />
                    </IconButton>
                  </Stack>
                  <Divider />
                  <Stack gap={3}>
                    <Stack gap={1}>
                      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                        <TextField
                          label="Mínimo"
                          type="number"
                          size="small"
                          value={xFaixa.min}
                          onChange={pEvent =>
                            handleUpdateFaixaNumerica(xFaixa.id, 'min', pEvent.target.value)
                          }
                          fullWidth
                          variant="outlined"
                        />
                        <TextField
                          label="Máximo"
                          type="number"
                          size="small"
                          value={xFaixa.max}
                          onChange={pEvent =>
                            handleUpdateFaixaNumerica(xFaixa.id, 'max', pEvent.target.value)
                          }
                          fullWidth
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>

                    <TextField
                      label="Discriminação"
                      placeholder="Critério interno"
                      value={xFaixa.discriminacao}
                      onChange={pEvent =>
                        handleUpdateFaixaNumerica(xFaixa.id, 'discriminacao', pEvent.target.value)
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Stack>
                  <Stack>
                    <Range
                      value={xFaixa.points}
                      onChange={pValue => handleUpdateFaixaNumerica(xFaixa.id, 'points', pValue)}
                    />
                    <QuestaoRating
                      readOnly
                      rating={xIndex}
                      max={xIndex === 0 ? 1 : values.faixasNumericas.length - 1}
                    />
                  </Stack>
                </Stack>
              ))}

            {/* Ação para adicionar item, variando entre opção e faixa conforme o tipo atual. */}
            {values.tipoResposta !== AVALIACAO_RESPOSTA_TIPO.DIC.id && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon iconName="insert" size={16} />}
                disabled={
                  values.interna || values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
                    ? values.opcoesMultiplaEscolha.length >= MAX_DISCRIMINACOES
                    : values.faixasNumericas.length >= MAX_DISCRIMINACOES
                }
                onClick={
                  values.interna || values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
                    ? handleAddMultiplaEscolha
                    : handleAddFaixaNumerica
                }>
                {values.interna || values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
                  ? 'Adicionar opção'
                  : 'Adicionar faixa'}
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Sidebar: seção da questão e ação redundante de salvar para ergonomia. */}
        <Stack
          component="sidebar"
          minWidth={{ lg: 320 }}
          maxWidth={{ lg: 420 }}
          gap={2}
          width="100%"
          sx={{
            position: { lg: 'sticky' },
            top: { lg: 16 },
            alignSelf: { lg: 'flex-start' }
          }}>
          <Card>
            <Stack gap={2} px={2} py={3}>
              <Stack gap={1}>
                <Typography
                  color="textPrimary"
                  variant="caption"
                  sx={{ textTransform: 'uppercase' }}>
                  SEÇÃO
                </Typography>
                <Select
                  label="Sessão"
                  value={xSessaoSelecionadaValida ? values.sessaoId : ''}
                  onChange={pEvent => formik.setFieldValue('sessaoId', Number(pEvent.target.value))}
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ 'aria-label': 'Sessão da questão' }}>
                  {sessoesQuestoes.map(xSessao => (
                    <MenuItem key={xSessao.sessao_id} value={xSessao.sessao_id}>
                      {xSessao.sessao}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </Stack>
          </Card>
          <Stack spacing={2}>
            <Button
              disabled={props.disabledButtons}
              variant="contained"
              color="primary"
              startIcon={<Icon iconName="upload" size={16} />}
              size="small"
              sx={{ px: 2 }}
              onClick={handleSave}>
              Salvar
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </BaseTemplate>
  );
});

AvaliacoesSettingsQuestoesEdit.displayName = 'AvaliacoesSettingsQuestoesEdit';

export default withMessage(AvaliacoesSettingsQuestoesEdit);
